/**
 * Telemetry event schemas (PROD-573).
 *
 * Zod schemas for the five Phase 1 event types specified in the master
 * prompt v3.3 §HC9 plus a meta-event for validation failure surfaced when
 * an emit attempt is rejected at the boundary. The schemas form the
 * structured-output contract per HC7 LLM Guardrails: nothing reaches a
 * sink without a successful parse.
 *
 * Per ADR-001 Layer 4, every emitted event is wrapped in a FHIR
 * AuditEvent-shaped envelope. The envelope is the universal frame; the
 * `entity` field carries the per-event payload that each ticket schema
 * already models.
 *
 * HC5: zero em / en dashes. Section separators use hyphens or commas.
 */
import { z } from 'zod';
import { TAB_IDS } from '../../layouts/tabs';

/**
 * Names of the events that carry a strict Zod schema in Phase 1. Every
 * other event name is accepted by `emitTelemetry` but routed through a
 * permissive schema so the existing PROD-568 / 569 / 570 / 571 / 572
 * call sites remain backwards-compatible.
 */
export const STRICT_EVENT_NAMES = [
  'mode_switch',
  'override_event',
  'tenant_change',
  'nba_confidence_adjusted',
  'lab_basket_confirmation_required',
  'llm_router_invoked',
  'llm_router_completed',
  'llm_router_fallback',
  'citation_validation_failed',
  'corpus_retrieval',
  'pathway_edit',
  'learning_loop_view',
  'app_init',
  'walkthrough_step_view',
  'walkthrough_completed',
  'walkthrough_dismissed',
  'uae_overlay_rendered',
  'uae_overlay_override',
] as const;

export type StrictEventName = (typeof STRICT_EVENT_NAMES)[number];

/** Meta-event emitted when a strict event fails its Zod parse. */
export const TELEMETRY_VALIDATION_FAILED = 'telemetry_validation_failed';

/**
 * FHIR R4 AuditEvent envelope per ADR-001 Layer 4. The envelope wraps
 * every emitted event; the per-event payload lives in `entity`.
 *
 * The shape is a Zod-friendly subset of the canonical FHIR AuditEvent
 * resource. Required fields (id, recorded, agent, source, entity,
 * correlation_id) carry the audit semantics; the remainder of the FHIR
 * resource is layered on by the App Insights collector at the platform
 * boundary.
 */
export const auditAgentSchema = z.object({
  type: z.enum(['system', 'practitioner', 'patient']),
  who: z.object({
    reference: z.string().min(1),
  }),
});

export type AuditAgent = z.infer<typeof auditAgentSchema>;

export const auditSourceSchema = z.object({
  site: z.literal('care-pathway-studio'),
  software: z.object({
    name: z.literal('cps-prototype'),
    version: z.string().min(1),
  }),
});

export type AuditSource = z.infer<typeof auditSourceSchema>;

/**
 * Universal envelope. `entity` is the event-specific payload; tested via
 * the per-event schemas below. `eventName` carries the strict event name
 * (or the meta-event name) for sink-side filtering.
 */
export const telemetryEnvelopeSchema = z.object({
  id: z.string().min(1),
  recorded: z.string().min(1),
  eventName: z.string().min(1),
  agent: auditAgentSchema,
  source: auditSourceSchema,
  correlation_id: z.string().min(1),
  entity: z.record(z.unknown()),
});

export type TelemetryEnvelope = z.infer<typeof telemetryEnvelopeSchema>;

/**
 * `mode_switch` (PROD-573 primary). Fires when `useModeStore.setMode` or
 * `useModeStore.toggle` flips between Demo and Live.
 */
export const modeSwitchSchema = z.object({
  from_mode: z.enum(['demo', 'live']),
  to_mode: z.enum(['demo', 'live']),
  latency_ms: z.number().min(0),
  correlation_id: z.string().min(1),
});

export type ModeSwitchPayload = z.infer<typeof modeSwitchSchema>;

/**
 * `override_event` (PROD-571). Fires from `useOverrideStore.submitOverride`
 * after the form values clear `overrideFormSchema`. The payload here is a
 * structural subset of the persisted `OverrideEvent`; the Zod parse here
 * enforces the on-the-wire contract independently of the form schema.
 */
export const overrideEventTelemetrySchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  reason_category: z.string().min(1),
  has_alternative_text: z.boolean(),
  has_context_tags: z.boolean(),
  correlation_id: z.string().min(1),
});

export type OverrideEventTelemetry = z.infer<typeof overrideEventTelemetrySchema>;

/**
 * `tenant_change` (PROD-568). Fires from `useTenantStore.setTenant` after
 * persistence succeeds.
 */
export const tenantChangeSchema = z.object({
  from_tenant: z.string().min(1),
  to_tenant: z.string().min(1),
  latency_ms: z.number().min(0),
  correlation_id: z.string().min(1),
});

export type TenantChangePayload = z.infer<typeof tenantChangeSchema>;

/**
 * `nba_confidence_adjusted` (PROD-572). Fires from
 * `applyOverrideToNba`. The shape mirrors the
 * `NbaConfidenceAdjustedTelemetry` interface in
 * `src/services/learning-loop.ts` so the schema parse is a no-op type
 * check rather than a transform.
 */
export const nbaConfidenceAdjustedSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  nba_id: z.string().min(1),
  tenant_id: z.string().min(1),
  reason_category: z.string().min(1),
  previous_confidence: z.number(),
  next_confidence: z.number(),
  previous_priority: z.enum(['high', 'medium', 'low']),
  next_priority: z.enum(['high', 'medium', 'low']),
  previous_state: z.enum(['active', 'reduced', 'restored']),
  next_state: z.enum(['active', 'reduced', 'restored']),
  override_count: z.number().int().min(0),
  floored: z.boolean(),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type NbaConfidenceAdjustedPayload = z.infer<
  typeof nbaConfidenceAdjustedSchema
>;

/**
 * `lab_basket_confirmation_required` (PROD-572). Fires from
 * `emitLabBasketConfirmationRequired` when a basket renders with
 * pre-selected items pending confirmation.
 */
export const labBasketConfirmationRequiredSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  pending_codes: z.array(z.string().min(1)),
  pending_count: z.number().int().min(0),
  ts: z.string().min(1),
});

export type LabBasketConfirmationRequiredPayload = z.infer<
  typeof labBasketConfirmationRequiredSchema
>;

/**
 * Meta-event payload emitted when a strict event fails Zod parse. The
 * original event does NOT reach any sink; this meta-event carries the
 * original name plus a flattened error report so the sink can record
 * the failure path.
 */
export const telemetryValidationFailedSchema = z.object({
  original_event_name: z.string().min(1),
  zod_errors: z.array(
    z.object({
      path: z.array(z.union([z.string(), z.number()])),
      message: z.string(),
      code: z.string(),
    }),
  ),
  correlation_id: z.string().min(1),
});

export type TelemetryValidationFailedPayload = z.infer<
  typeof telemetryValidationFailedSchema
>;

/**
 * Lookup table from event name to its strict schema. `emitTelemetry`
 * consults this map; a hit triggers strict parse and a miss falls back
 * to the permissive `passthrough` schema.
 */
/**
 * `llm_router_invoked` (PROD-589). Fires on every routeNbaSuggestion
 * entry. The `retrieved_chunk_count` field is surfaced so the dashboard
 * can correlate small retrieval sets with downstream fallback rates.
 */
export const llmRouterInvokedSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  patient_id: z.string().min(1),
  retrieved_chunk_count: z.number().int().min(0),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type LlmRouterInvokedPayload = z.infer<typeof llmRouterInvokedSchema>;

/**
 * `llm_router_completed` (PROD-589). Fires after the structured-output
 * + citation validation pass and before the result reaches the caller.
 */
export const llmRouterCompletedSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  patient_id: z.string().min(1),
  cited_chunk_count: z.number().int().min(0),
  confidence: z.number().min(0).max(1),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type LlmRouterCompletedPayload = z.infer<
  typeof llmRouterCompletedSchema
>;

/**
 * `llm_router_fallback` (PROD-589). Fires on every non-success path.
 * The reason vocabulary is the closed enum from llm-router-types.ts.
 */
export const llmRouterFallbackSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  patient_id: z.string().min(1),
  reason: z.enum([
    'timeout',
    'parse_failed',
    'schema_failed',
    'orphan_citation',
    'tier_violation',
    'no_api_key',
    'upstream_error',
  ]),
  correlation_id: z.string().min(1),
});

export type LlmRouterFallbackPayload = z.infer<typeof llmRouterFallbackSchema>;

/**
 * `citation_validation_failed` (PROD-589). Fires when the LLM cites a
 * chunk_id that is not present in the retrieved set. Carries the
 * orphan ids + the retrieved id list so the dashboard can render the
 * delta inline.
 */
export const citationValidationFailedSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  patient_id: z.string().min(1),
  orphan_chunk_ids: z.array(z.string().min(1)).min(1),
  retrieved_chunk_ids: z.array(z.string().min(1)),
  correlation_id: z.string().min(1),
});

export type CitationValidationFailedPayload = z.infer<
  typeof citationValidationFailedSchema
>;

/**
 * `corpus_retrieval` (PROD-590). Fires every time the citation resolver
 * invokes (or short-circuits) `retrieveSimilar` for an NBA candidate. The
 * payload is `mode`-tagged so the dashboard can split Live vs Demo
 * volumes; `chunk_ids` and `distances` are always parallel arrays so
 * downstream tooling can join them by index.
 */
export const corpusRetrievalSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  tenant_id: z.string().min(1),
  patient_id: z.string().min(1),
  nba_id: z.string().min(1),
  mode: z.enum(['live', 'demo']),
  k: z.number().int().min(0),
  threshold: z.number().min(0).max(1),
  chunk_ids: z.array(z.string().min(1)),
  distances: z.array(z.number().min(0).max(2)),
  result_count: z.number().int().min(0),
  latency_ms: z.number().min(0),
  source: z.enum(['live', 'demo', 'empty']),
  empty_reason: z.string().nullable(),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type CorpusRetrievalPayload = z.infer<typeof corpusRetrievalSchema>;

/**
 * `pathway_edit` (PROD-591). Fires whenever the Pathway Editor (pathway
 * store `editNode` / `editLabBasketItem`) commits a change. Carries the
 * before / after JSON-serialized snapshots so the dashboard can diff the
 * mutation without re-running the editor. The `mode` field is always
 * present per HC1 two-mode operation; live-mode edits are flagged so
 * the learning loop can prioritize them.
 *
 * `before` and `after` are unconstrained JSON-serializable values; the
 * editor encodes the field-level change (string, boolean, array). The
 * envelope schema does not introspect the shape because the editor
 * supports multiple property types in a single event vocabulary.
 */
export const pathwayEditSchema = z.object({
  pathway_id: z.string().min(1),
  node_id: z.string().min(1),
  edit_type: z.enum([
    'node_property',
    'lab_basket_preselected',
    'lab_basket_requires_confirmation',
    'lab_basket_rationale',
    'lab_basket_item',
  ]),
  before: z.unknown(),
  after: z.unknown(),
  mode: z.enum(['demo', 'live']),
  pathway_edit_live: z.boolean(),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type PathwayEditPayload = z.infer<typeof pathwayEditSchema>;

/**
 * `learning_loop_view` (PROD-592). Fires every time the Learning Loop
 * dashboard mounts AND on every refresh tick. Carries the active mode,
 * the tenant the dashboard is scoped to, the configured refresh
 * interval, the count of metric blocks rendered, and a flag that
 * indicates whether the latest snapshot was sourced from a real
 * telemetry sink (Live Mode with the App Insights SDK bundled) or
 * fell back to Demo. The dashboard emits this event at every refresh
 * so the platform can trace mount + tick cadence per HC9.
 */
export const learningLoopViewSchema = z.object({
  mode: z.enum(['demo', 'live']),
  tenant_id: z.string().min(1),
  refresh_interval_ms: z.number().int().min(0),
  blocks_rendered: z.number().int().min(0),
  has_live_data: z.boolean(),
  trigger: z.enum(['mount', 'tick', 'manual']),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type LearningLoopViewPayload = z.infer<typeof learningLoopViewSchema>;

/**
 * `app_init` (PROD-582). Fires once per AppShell mount with the resolved
 * runtime context. The `entryTab` field carries the tab id derived from
 * the entry URL so telemetry consumers can reconstruct deep-link landing
 * for sessions where the route-change `tab_change` guard short-circuits
 * (from === to on first paint). Promoted to a strict event in PROD-582
 * so the payload is parsed at the boundary; HC9 telemetry-real requires
 * the Zod schema reject malformed payloads.
 *
 * `entryTab` is a closed enum drawn from `TAB_IDS` in
 * `src/layouts/tabs.ts` to keep a single source of truth for the
 * canonical tab vocabulary.
 */
export const appInitSchema = z.object({
  tenantDefault: z.string().min(1),
  mode: z.enum(['demo', 'live']),
  userId: z.string().min(1),
  entryTab: z.enum(TAB_IDS),
  correlation_id: z.string().min(1),
});

export type AppInitPayload = z.infer<typeof appInitSchema>;

/**
 * `walkthrough_step_view` (PROD-595). Fires every time the demo walkthrough
 * coachmark advances to a step (including the auto-shown first step on
 * mount and every Next / Previous transition). Carries the step id, the
 * 0-based index, the total step count for the active script, the source
 * that triggered the walkthrough (auto first-visit vs manual relaunch
 * via the TopBar), and a correlation id that groups the per-session
 * step-view + completed / dismissed events.
 */
export const walkthroughStepViewSchema = z.object({
  step_id: z.string().min(1),
  step_index: z.number().int().min(0),
  total_steps: z.number().int().min(1),
  source: z.enum(['auto', 'manual']),
  correlation_id: z.string().min(1),
});

export type WalkthroughStepViewPayload = z.infer<
  typeof walkthroughStepViewSchema
>;

/**
 * `walkthrough_completed` (PROD-595). Fires once when the user advances
 * past the final step (Next on the last coachmark). Carries the total
 * step count, the elapsed milliseconds from first step view to final
 * advance, and the same correlation id used for the step-view events
 * so the dashboard can stitch the funnel together.
 */
export const walkthroughCompletedSchema = z.object({
  total_steps: z.number().int().min(1),
  completion_time_ms: z.number().min(0),
  correlation_id: z.string().min(1),
});

export type WalkthroughCompletedPayload = z.infer<
  typeof walkthroughCompletedSchema
>;

/**
 * `walkthrough_dismissed` (PROD-595). Fires when the user clicks Skip,
 * presses ESC, or otherwise dismisses the coachmark before completion.
 * Carries the step id + index where the dismissal occurred plus the
 * shared correlation id.
 */
export const walkthroughDismissedSchema = z.object({
  step_id: z.string().min(1),
  step_index: z.number().int().min(0),
  correlation_id: z.string().min(1),
});

export type WalkthroughDismissedPayload = z.infer<
  typeof walkthroughDismissedSchema
>;

/**
 * `uae_overlay_rendered` (PROD-658). Fires every time the UAE-overlay
 * resolver activates one or more overlay bundles for a patient render.
 * Source discriminator is `pathway-uae-overlay` per HC9 strict registry.
 *
 * Distinct from the global `corpus_retrieval` event (source=pathway-studio
 * or similar) so the UAE-overlay stream is independently queryable.
 */
export const uaeOverlayRenderedSchema = z.object({
  source: z.literal('pathway-uae-overlay'),
  patient_id: z.string().min(1),
  pathway_id: z.string().min(1),
  overlay_id: z.string().min(1),
  overlay_label: z.string().min(1),
  triggered_by: z.string().min(1),
  ethnic_subgroup: z.string().nullable(),
  ramadan_observant: z.boolean(),
  nba_count: z.number().int().min(0),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type UaeOverlayRenderedPayload = z.infer<typeof uaeOverlayRenderedSchema>;

/**
 * `uae_overlay_override` (PROD-658). Fires when a clinician overrides an
 * NBA that originated from a UAE overlay bundle. Carries overlay context
 * (`overlay_id`, `ethnic_subgroup`, `ramadan_observant`) in the payload
 * so `Provenance.entity[]` can reference the overlay source.
 *
 * Distinct from `override_event` (which fires for all NBA overrides) so
 * the overlay-specific override stream is independently queryable.
 */
export const uaeOverlayOverrideSchema = z.object({
  source: z.literal('pathway-uae-overlay'),
  patient_id: z.string().min(1),
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  nba_id: z.string().min(1),
  overlay_id: z.string().min(1),
  ethnic_subgroup: z.string().nullable(),
  ramadan_observant: z.boolean(),
  reason_category: z.string().min(1),
  has_alternative_text: z.boolean(),
  correlation_id: z.string().min(1),
  ts: z.string().min(1),
});

export type UaeOverlayOverridePayload = z.infer<typeof uaeOverlayOverrideSchema>;

export const STRICT_EVENT_SCHEMAS: Record<StrictEventName, z.ZodTypeAny> = {
  mode_switch: modeSwitchSchema,
  override_event: overrideEventTelemetrySchema,
  tenant_change: tenantChangeSchema,
  nba_confidence_adjusted: nbaConfidenceAdjustedSchema,
  lab_basket_confirmation_required: labBasketConfirmationRequiredSchema,
  llm_router_invoked: llmRouterInvokedSchema,
  llm_router_completed: llmRouterCompletedSchema,
  llm_router_fallback: llmRouterFallbackSchema,
  citation_validation_failed: citationValidationFailedSchema,
  corpus_retrieval: corpusRetrievalSchema,
  pathway_edit: pathwayEditSchema,
  learning_loop_view: learningLoopViewSchema,
  app_init: appInitSchema,
  walkthrough_step_view: walkthroughStepViewSchema,
  walkthrough_completed: walkthroughCompletedSchema,
  walkthrough_dismissed: walkthroughDismissedSchema,
  uae_overlay_rendered: uaeOverlayRenderedSchema,
  uae_overlay_override: uaeOverlayOverrideSchema,
};

/** Type predicate for strict event names (cheap; closed enum). */
export function isStrictEventName(name: string): name is StrictEventName {
  return (STRICT_EVENT_NAMES as readonly string[]).includes(name);
}
